import { skipToken } from '@apollo/client/react';
import NextImage, { ImageLoaderProps } from 'next/image';
import ContentLoader from 'react-content-loader';

import { useRutilusQuery } from '@graphql/client/useRutilusQuery';
import { Avatar, AvatarSize, ProStarSize } from '../../../../components/Avatar/Avatar';
import { BlogAuthorLink } from '../../../../components/Blog/BlogAuthorButton/BlogAuthorLink';
import { FollowButton } from '../../../../components/Clickables/Buttons/FollowButton';
import { InternalLink } from '../../../../components/Clickables/InternalLink/InternalLink';
import { Heading } from '../../../../components/Heading/Heading';
import { FishCatchesIcon } from '../../../../components/Icons/FishCatchesIcon';
import { Text } from '../../../../components/Text/Text';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';
import { getImgProxyLoader } from '@lib/images/imgProxyLoader';
import { getCountryFlag } from '@domain/user/getCountryFlag';
import { getCountryFullText } from '@domain/user/getCountryFullText';
import { GetUserIsFollowedVariables, ImageFields } from '../../../../generated/graphql';
import { getUserIsFollowed } from '@graphql/queries/rutilus/Users';
import imagePlaceholder from '../../assets/imagePlaceholder.svg';
import { AnglerBio } from '../AnglerBio/AnglerBio';

interface IProps {
  coverImage: ImageFields | null;
  avatar: string;
  nickname: string;
  fullName: string;
  countryCode: string;
  biography: string | null;
  anglerId: string;
  currentUserNickname: string | null;
  isPremium: boolean;
  blogSlug: string | null;
  totalAnglerCatches: number;
}

export const AnglerDetails = ({
  coverImage,
  nickname,
  avatar,
  fullName,
  countryCode,
  biography,
  anglerId,
  currentUserNickname,
  isPremium,
  blogSlug,
  totalAnglerCatches,
}: IProps) => {
  const clientHasRendered = useClientHasRendered();
  const variables: GetUserIsFollowedVariables = { nickname };
  const { data: followData, loading } = useRutilusQuery(
    getUserIsFollowed,
    !nickname || !clientHasRendered
      ? skipToken
      : {
          variables,
        },
  );

  const followedByCurrentUser = followData?.user.followedByCurrentUser ?? false;

  const imgUrl = coverImage?.url;
  const imageWidth = coverImage?.width ?? 0;
  const imageHeight = coverImage?.height;

  const imgProxyLoader = ({ src, width, quality }: ImageLoaderProps): string =>
    getImgProxyLoader(src, width, imageWidth, imageHeight, { quality });

  const imageSrc = coverImage && imgUrl ? imgUrl : imagePlaceholder;

  return (
    <div className="mb-8 flex w-full flex-col items-center text-center md:sticky md:top-20 md:h-max md:w-[400px]">
      <div className="mb-6 grid h-[220px] w-full grid-cols-1 grid-rows-1">
        <div className="relative col-start-1 row-start-1 size-full">
          <NextImage
            alt="background"
            className="size-full object-cover md:rounded-t-md"
            src={imageSrc}
            loader={coverImage && imgUrl ? imgProxyLoader : undefined}
            fill
            sizes="(min-width: 990px) 40vw, 100vw"
          />
        </div>
        <div className="col-start-1 row-start-1 flex place-content-center items-end text-center">
          <Avatar
            alt={nickname}
            src={avatar}
            size={AvatarSize.EXTRA_LARGE}
            starSize={ProStarSize.EXTRA_LARGE}
            className="border-2 border-solid border-white shadow-md"
            isPro={isPremium}
          />
        </div>
      </div>

      <Heading as="h1" className="col-start-1 row-start-4 m-0 align-bottom md:px-4" variant="md">
        {fullName}
      </Heading>
      <Text
        className="text-primary col-start-1 row-start-5 flex flex-col items-center justify-center"
        variant="sm"
      >
        <span>@{nickname}</span>
        <span>
          {getCountryFlag(countryCode)}&ensp;{getCountryFullText(countryCode)}
        </span>
      </Text>

      {totalAnglerCatches > 0 && (
        <div className="flex w-full items-center justify-center gap-2">
          <FishCatchesIcon size={40} />
          <Text className="mb-0 font-semibold">{totalAnglerCatches}</Text>
        </div>
      )}

      <AnglerBio
        biography={biography}
        anglerId={anglerId}
        className="col-start-1 row-start-6 sm:mx-auto sm:w-4/5"
      />

      <div className="mx-auto flex w-[300px] flex-col justify-center gap-4 sm:mx-12 md:pb-6">
        {!clientHasRendered ||
          (loading && (
            <ContentLoader
              uniqueKey="profile-follow-button-loader"
              className="flex sm:hidden"
              width={300}
              height={35}
            >
              <rect x="0" y="0" rx="5" ry="5" width={300} height={35} />
            </ContentLoader>
          ))}
        {clientHasRendered && !loading && currentUserNickname !== nickname && (
          <FollowButton
            source="user-page-follow-button"
            id={anglerId}
            isFollowed={followedByCurrentUser}
            buttonProps={{ className: 'grow' }}
            customAppDownloadHeader={
              followedByCurrentUser
                ? `Download the Fishbrain app to unfollow ${nickname}`
                : `Download the Fishbrain app to follow ${nickname}`
            }
          />
        )}
        {clientHasRendered && !loading && currentUserNickname === nickname && (
          <InternalLink to="/profile" className="w-full" variant="buttonSecondary" size="sm">
            Edit profile
          </InternalLink>
        )}
        {blogSlug && nickname && <BlogAuthorLink blogSlug={blogSlug} authorNickname={nickname} />}
      </div>
    </div>
  );
};
